import React, { useState, useMemo, useEffect } from 'react';
import { Filter, Layers, X } from 'lucide-react';
import type { PortfolioComponentData } from '../../services/portfolioComponentService';
import { getModelColor, type ComponentFilter, type ModelWithColor } from './portfolioMapUtils';

interface ComponentFilterPanelProps {
  portfolioData: PortfolioComponentData | null;
  filter: ComponentFilter;
  onFilterChange: (filter: ComponentFilter) => void;
  onModelsChange?: (models: ModelWithColor[]) => void;
}

const ComponentFilterPanel: React.FC<ComponentFilterPanelProps> = ({
  portfolioData,
  filter,
  onFilterChange,
  onModelsChange,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Only show Inverters and Modules categories
  const ALLOWED_CATEGORIES = ['inverter', 'module'];

  const categories = useMemo(() => {
    if (!portfolioData) return [];
    return portfolioData.summaries
      .filter(s => ALLOWED_CATEGORIES.includes(s.component_category))
      .map(s => ({
        category: s.component_category,
        displayName: s.display_name,
        count: s.total_count,
        siteCount: s.site_count,
        color: s.color,
      }));
  }, [portfolioData]);

  // Total count only for allowed categories
  const filteredTotalCount = useMemo(() => {
    if (!portfolioData) return 0;
    return portfolioData.summaries
      .filter(s => ALLOWED_CATEGORIES.includes(s.component_category))
      .reduce((sum, s) => sum + s.total_count, 0);
  }, [portfolioData]);

  const selectedCategorySummary = useMemo(() => {
    if (!portfolioData || !filter.category) return null;
    return portfolioData.summaries.find(s => s.component_category === filter.category) || null;
  }, [portfolioData, filter.category]);

  // Aggregate models grouped by normalized name with site count and colors
  const models = useMemo((): ModelWithColor[] => {
    if (!selectedCategorySummary) return [];

    const modelGroups = new Map<string, {
      displayModel: string;
      totalCount: number;
      siteCount: number;
      siteIds: Set<string>;
      originalModels: string[];
    }>();

    for (const m of selectedCategorySummary.models) {
      const groupKey = m.model
        .toLowerCase()
        .replace(/[-_#]\d+$/, '')
        .replace(/\s+\d{1,2}$/, '')
        .replace(/[^a-z0-9\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      if (!modelGroups.has(groupKey)) {
        modelGroups.set(groupKey, {
          displayModel: m.model,
          totalCount: 0,
          siteCount: 0,
          siteIds: new Set(),
          originalModels: [],
        });
      }

      const group = modelGroups.get(groupKey)!;
      group.totalCount += m.total_count;
      group.originalModels.push(m.model);

      for (const siteDist of m.site_distribution) {
        group.siteIds.add(siteDist.site_id);
      }
    }

    return Array.from(modelGroups.values())
      .map((g, index) => ({
        model: g.displayModel,
        total_count: g.totalCount,
        site_count: g.siteIds.size,
        siteIds: g.siteIds,
        originalModels: g.originalModels,
        color: getModelColor(g.displayModel, index),
      }))
      .sort((a, b) => b.total_count - a.total_count)
      .slice(0, 20);
  }, [selectedCategorySummary]);

  // Notify parent of models change for map coloring
  useEffect(() => {
    if (onModelsChange) {
      onModelsChange(models);
    }
  }, [models, onModelsChange]);

  if (!portfolioData) return null;

  if (isCollapsed) {
    return (
      <button
        onClick={() => setIsCollapsed(false)}
        className="absolute top-4 right-4 z-[1000] bg-[var(--color-surface)] border border-[#17c480]/30 rounded-lg p-2 hover:bg-[var(--color-surface-hover)] transition-colors shadow-lg"
      >
        <Filter className="w-5 h-5 text-[#17c480]" />
      </button>
    );
  }

  return (
    <div className="absolute top-4 right-4 z-[1000] w-72 bg-[var(--color-surface)]/95 backdrop-blur border border-[#17c480]/30 rounded-xl shadow-2xl overflow-hidden">
      <div className="flex items-center justify-between p-3 border-b border-[#17c480]/20">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-[#17c480]" />
          <span className="text-sm font-medium text-[var(--color-text-primary)]">Filter Components</span>
        </div>
        <button
          onClick={() => setIsCollapsed(true)}
          className="p-1 hover:bg-[var(--color-surface-hover)] rounded transition-colors"
        >
          <X className="w-4 h-4 text-[var(--color-text-muted)]" />
        </button>
      </div>

      <div className="p-3 space-y-3 max-h-96 overflow-auto">
        {/* Category Filter */}
        <div>
          <label className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider mb-1.5 block">
            Component Type
          </label>
          <div className="space-y-1">
            <button
              onClick={() => onFilterChange({ category: null, manufacturer: null, model: null })}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left transition-colors ${
                !filter.category
                  ? 'bg-[#17c480]/20 border border-[#17c480]/30'
                  : 'hover:bg-[var(--color-surface-hover)] border border-transparent'
              }`}
            >
              <Layers className="w-3.5 h-3.5 text-[#17c480]" />
              <span className="text-xs text-[var(--color-text-primary)] flex-1">All Components</span>
              <span className="text-xs text-[var(--color-text-muted)]">
                {filteredTotalCount.toLocaleString()}
              </span>
            </button>
            {categories.map(cat => (
              <button
                key={cat.category}
                onClick={() => onFilterChange({ category: cat.category, manufacturer: null, model: null })}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left transition-colors ${
                  filter.category === cat.category
                    ? 'bg-[#17c480]/20 border border-[#17c480]/30'
                    : 'hover:bg-[var(--color-surface-hover)] border border-transparent'
                }`}
              >
                <div className="w-3 h-3 rounded" style={{ backgroundColor: cat.color }} />
                <span className="text-xs text-[var(--color-text-primary)] flex-1 truncate">
                  {cat.displayName}
                </span>
                <span className="text-xs text-[var(--color-text-muted)]">
                  {cat.count.toLocaleString()}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Model Filter */}
        {filter.category && models.length > 0 && (
          <div>
            <label className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider mb-1.5 block">
              Model
            </label>
            <div className="space-y-1">
              <button
                onClick={() => onFilterChange({ ...filter, model: null })}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left transition-colors ${
                  !filter.model
                    ? 'bg-[#17c480]/20 border border-[#17c480]/30'
                    : 'hover:bg-[var(--color-surface-hover)] border border-transparent'
                }`}
              >
                <span className="text-xs text-[var(--color-text-primary)] flex-1">All Models</span>
              </button>
              {models.slice(0, 8).map((m, idx) => (
                <button
                  key={`${m.model}-${idx}`}
                  onClick={() => onFilterChange({ ...filter, model: m.model })}
                  className={`w-full flex items-start gap-2 px-2 py-1.5 rounded text-left transition-colors ${
                    filter.model === m.model
                      ? 'bg-[#17c480]/20 border border-[#17c480]/30'
                      : 'hover:bg-[var(--color-surface-hover)] border border-transparent'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-[var(--color-text-primary)] truncate block">
                      {m.model}
                    </span>
                    <span className="text-[10px] text-[var(--color-text-muted)]">
                      {m.site_count} {m.site_count === 1 ? 'site' : 'sites'} &bull; {m.total_count.toLocaleString()} total
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Clear Filter */}
        {(filter.category || filter.model) && (
          <button
            onClick={() => onFilterChange({ category: null, manufacturer: null, model: null })}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-[var(--color-bg-tertiary)] text-[var(--color-text-muted)] text-xs rounded-lg hover:bg-[var(--color-surface-hover)] transition-colors"
          >
            <X className="w-3.5 h-3.5" />
            Clear Filters
          </button>
        )}
      </div>
    </div>
  );
};

export default ComponentFilterPanel;
